import moment from 'moment';
import Swal from 'sweetalert2';
import HelperMixin from '../../Shared/HelperMixin.vue';
import PaginationHelper from '../../Shared/PaginationHelper.vue';

const KIND_OPTIONS = [
    { value: 1, label: 'مخزن رئيسي' },
    { value: 2, label: 'مخزن فرعي' },
    { value: 4, label: 'مخزن تالف' },
];

export default {
    name: 'WarehouseManagement',
    mixins: [HelperMixin],
    components: { PaginationHelper },

    async created() {
        await this.CheckLoginStatus();
        if (!this.canViewPage) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'ليس لديك صلاحية للوصول إلى إدارة المخازن');
            this.href('/Home');
            return;
        }

        await this.loadBranches();
        await this.GetStatistics();
        this.GetInfo(1);
    },

    computed: {
        canViewPage() {
            return this.hasInventoryAndStockAccessUi()
                || (this.isFacilityCenterRole() && this.loginDetails && this.loginDetails.branchesId);
        },
        canManagePage() {
            return this.hasInventoryAndStockAccessUi();
        },
        canDeletePage() {
            return this.hasAdministratorOperationalAccessUi();
        },
        statsCards() {
            const s = this.Statistics || {};
            return [
                this.statCard('إجمالي المخازن', s.total, 'primary', 'ti ti-building-warehouse'),
                this.statCard('المخازن النشطة', s.active, 'success', 'ti ti-circle-check'),
                this.statCard('المخازن غير النشطة', s.notActive, 'warning', 'ti ti-circle-x'),
                this.statCard('مخازن تابعة للوزارة', s.ministry, 'info', 'ti ti-building-bank'),
            ];
        },
        kindOptions() {
            return KIND_OPTIONS;
        },
        showBranchSelector() {
            return this.canManagePage;
        },
        showDepartmentSelector() {
            return !!(this.ruleForm.BranchesId && Number(this.ruleForm.BranchesId) > 0);
        },
    },

    filters: {
        moment(date) {
            if (!date) return '—';
            return moment(date).format('DD/MM/YYYY');
        },
    },

    data() {
        return {
            pageNo: 1,
            pageSize: 10,
            pages: 0,
            Search: '',

            FilterScope: '',
            FilterKind: '',
            FilterStatus: '',

            Info: [],
            Statistics: {},
            branchesList: [],
            departmentsList: [],
            departmentsLoading: false,

            ScreenTitle: 'إدارة المخازن',

            ruleForm: this.buildEmptyForm(),
            editMode: false,
            currentItem: null,

            rules: {
                Name: [
                    { required: true, message: 'الرجاء إدخال اسم المخزن', trigger: 'blur' },
                ],
                StockingKind: [
                    { required: true, message: 'الرجاء اختيار نوع المخزن', trigger: 'change' },
                ],
            },
        };
    },

    methods: {
        buildEmptyForm() {
            return {
                Id: null,
                Name: '',
                Descriptions: '',
                BranchesId: '',
                FacilityDepartmentId: '',
                StockingKind: '',
            };
        },

        tableRowNumber(index) {
            return ((Number(this.pageNo) - 1) * Number(this.pageSize)) + index + 1;
        },

        scopeLabel(item) {
            if (!item) return '';
            return item.isCentral ? 'وزارة الصحة' : (item.branchName || '—');
        },

        kindLabel(item) {
            return (item && item.stockingKindLabel) || '';
        },

        kindBadgeClass(kind) {
            if (kind === 1) return 'kind-chip';
            if (kind === 2) return 'kind-chip kind-secondary';
            if (kind === 4) return 'kind-chip kind-damaged';
            return 'kind-chip';
        },

        async loadBranches() {
            try {
                const res = await this.$http.GetAllBranches();
                this.branchesList = (res && res.data && res.data.info) || [];
            } catch {
                this.branchesList = [];
            }
        },

        async loadDepartments(branchId) {
            if (!branchId) {
                this.departmentsList = [];
                return;
            }
            this.departmentsLoading = true;
            try {
                const res = await this.$http.GetAllFacilityDepartments(branchId, false);
                this.departmentsList = (res && res.data && res.data.info) || [];
            } catch {
                this.departmentsList = [];
            } finally {
                this.departmentsLoading = false;
            }
        },

        onBranchChanged() {
            this.ruleForm.FacilityDepartmentId = '';
            const branchId = this.ruleForm.BranchesId;
            if (branchId) {
                this.loadDepartments(Number(branchId));
            } else {
                this.departmentsList = [];
            }
        },

        ApplyFilters() {
            this.GetInfo(1);
        },

        ResetFilters() {
            this.Search = '';
            this.FilterScope = '';
            this.FilterKind = '';
            this.FilterStatus = '';
            this.GetInfo(1);
        },

        GetStatistics() {
            return this.$http.GetWarehouseStatistics()
                .then(res => {
                    this.Statistics = res.data || {};
                })
                .catch(() => {
                    this.Statistics = {};
                });
        },

        GetInfo(pageNo) {
            this.pageNo = pageNo || this.pageNo || 1;
            if (!this.Search) this.$blockUI.Start();
            this.$http.GetWarehouses(
                this.pageNo,
                this.pageSize,
                this.Search,
                this.FilterScope || null,
                this.FilterKind === '' ? null : Number(this.FilterKind),
                this.FilterStatus === '' ? null : Number(this.FilterStatus),
            )
                .then(res => {
                    if (!this.Search) this.$blockUI.Stop();
                    this.Info = (res.data && res.data.info) || [];
                    this.pages = (res.data && res.data.count) || 0;
                })
                .catch(() => {
                    if (!this.Search) this.$blockUI.Stop();
                    this.Info = [];
                    this.pages = 0;
                });
        },

        OpenAddDialog() {
            this.editMode = false;
            this.currentItem = null;
            this.ruleForm = this.buildEmptyForm();
            this.departmentsList = [];
        },

        OpenEditDialog(item) {
            this.editMode = true;
            this.currentItem = item;
            this.ruleForm = {
                Id: item.id,
                Name: item.name || '',
                Descriptions: item.descriptions || '',
                BranchesId: item.branchesId != null ? String(item.branchesId) : '',
                FacilityDepartmentId: item.facilityDepartmentId != null ? String(item.facilityDepartmentId) : '',
                StockingKind: item.stockingKind != null ? String(item.stockingKind) : '',
            };
            if (item.branchesId) {
                this.loadDepartments(Number(item.branchesId));
            } else {
                this.departmentsList = [];
            }
        },

        buildPayload() {
            return {
                Id: this.ruleForm.Id ? Number(this.ruleForm.Id) : 0,
                Name: (this.ruleForm.Name || '').trim(),
                Descriptions: this.ruleForm.Descriptions || '',
                BranchesId: this.ruleForm.BranchesId === '' || this.ruleForm.BranchesId === null || this.ruleForm.BranchesId === undefined
                    ? null
                    : Number(this.ruleForm.BranchesId),
                FacilityDepartmentId: this.ruleForm.FacilityDepartmentId === '' || this.ruleForm.FacilityDepartmentId === null || this.ruleForm.FacilityDepartmentId === undefined
                    ? null
                    : Number(this.ruleForm.FacilityDepartmentId),
                StockingKind: this.ruleForm.StockingKind === '' || this.ruleForm.StockingKind === null || this.ruleForm.StockingKind === undefined
                    ? null
                    : Number(this.ruleForm.StockingKind),
            };
        },

        SubmitForm(formName) {
            this.$refs[formName].validate((valid) => {
                if (!valid) {
                    this.$helper.showSwal('warning');
                    return false;
                }
                const payload = this.buildPayload();
                this.$blockUI.Start();
                const request = this.editMode
                    ? this.$http.EditWarehouse(payload)
                    : this.$http.AddWarehouse(payload);
                request
                    .then(res => {
                        this.$blockUI.Stop();
                        const msg = (res && res.data && (res.data.message || res.data)) || 'تمت العملية بنجاح';
                        this.$helper.ShowMessage('success', 'عملية ناجحة', msg);
                        this.$helper.DestroyElemntById('CloseWarehouseModal');
                        this.GetInfo(this.editMode ? this.pageNo : 1);
                        this.GetStatistics();
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        const msg = err && err.response && err.response.data ? err.response.data : 'حدث خطأ غير متوقع';
                        this.$helper.ShowMessage('error', this.editMode ? 'خطأ بعملية التعديل' : 'خطأ بعملية الإضافة', msg);
                    });
            });
        },

        ToggleStatus(item) {
            const action = item.isActive ? 'تعطيل' : 'تنشيط';
            Swal.fire({
                title: 'تأكيد العملية',
                text: `هل تريد ${action} المخزن "${item.name}"؟`,
                icon: 'question',
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-primary me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'تأكيد',
                cancelButtonText: 'إلغاء',
            }).then((result) => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.ToggleWarehouseStatus(item.id)
                    .then(res => {
                        this.$blockUI.Stop();
                        const msg = (res && res.data && (res.data.message || res.data)) || 'تم تغيير الحالة بنجاح';
                        this.$helper.ShowMessage('success', 'عملية ناجحة', msg);
                        this.GetInfo(this.pageNo);
                        this.GetStatistics();
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        const msg = err && err.response && err.response.data ? err.response.data : 'حدث خطأ غير متوقع';
                        this.$helper.ShowMessage('error', 'خطأ بالعملية', msg);
                    });
            });
        },

        ToggleFreeze(item) {
            const action = item.isInventoryFrozen ? 'رفع التجميد عن' : 'تجميد';
            Swal.fire({
                title: 'تأكيد العملية',
                text: `هل تريد ${action} المخزن "${item.name}" للجرد؟`,
                icon: 'question',
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-primary me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'تأكيد',
                cancelButtonText: 'إلغاء',
            }).then((result) => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.ToggleWarehouseFreeze(item.id)
                    .then(res => {
                        this.$blockUI.Stop();
                        const msg = (res && res.data && (res.data.message || res.data)) || 'تم تنفيذ العملية';
                        this.$helper.ShowMessage('success', 'عملية ناجحة', msg);
                        this.GetInfo(this.pageNo);
                        this.GetStatistics();
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        const msg = err && err.response && err.response.data ? err.response.data : 'حدث خطأ غير متوقع';
                        this.$helper.ShowMessage('error', 'خطأ بالعملية', msg);
                    });
            });
        },

        Delete(item) {
            Swal.fire({
                title: 'تأكيد الحذف',
                text: `هل تريد حذف المخزن "${item.name}"؟`,
                icon: 'warning',
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-danger me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'حذف',
                cancelButtonText: 'إلغاء',
            }).then((result) => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.DeleteWarehouse(item.id)
                    .then(res => {
                        this.$blockUI.Stop();
                        const msg = (res && res.data && (res.data.message || res.data)) || 'تم الحذف بنجاح';
                        this.$helper.ShowMessage('success', 'عملية ناجحة', msg);
                        this.GetInfo(this.pageNo);
                        this.GetStatistics();
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        const msg = err && err.response && err.response.data ? err.response.data : 'حدث خطأ غير متوقع';
                        this.$helper.ShowMessage('error', 'خطأ بعملية الحذف', msg);
                    });
            });
        },

        ExportExcel() {
            if (!this.Info || this.Info.length === 0) {
                this.$helper.ShowMessage('warning', 'لا توجد بيانات', 'لا توجد سجلات للتصدير');
                return;
            }
            const headers = ['#', 'اسم المخزن', 'الجهة', 'القسم', 'النوع', 'الحالة', 'مجمد للجرد', 'تاريخ الإنشاء'];
            const rows = this.Info.map((item, index) => [
                this.tableRowNumber(index),
                item.name || '',
                this.scopeLabel(item),
                item.departmentName || '—',
                item.stockingKindLabel || '—',
                item.isActive ? 'نشط' : 'غير نشط',
                item.isInventoryFrozen ? 'نعم' : 'لا',
                item.createdOn ? moment(item.createdOn).format('DD/MM/YYYY') : '—',
            ]);
            const csv = [headers, ...rows]
                .map(line => line.map(cell => {
                    const s = String(cell == null ? '' : cell).replace(/"/g, '""');
                    return `"${s}"`;
                }).join(','))
                .join('\n');
            const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `warehouses-${moment().format('YYYYMMDD-HHmm')}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        },

        ExportPdf() {
            if (!this.Info || this.Info.length === 0) {
                this.$helper.ShowMessage('warning', 'لا توجد بيانات', 'لا توجد سجلات للتصدير');
                return;
            }
            const rows = this.Info.map((item, index) => `
                <tr>
                    <td>${this.tableRowNumber(index)}</td>
                    <td>${this.$helper.SafeHtml ? this.$helper.SafeHtml(item.name) : (item.name || '')}</td>
                    <td>${this.scopeLabel(item)}</td>
                    <td>${item.departmentName || '—'}</td>
                    <td>${item.stockingKindLabel || '—'}</td>
                    <td>${item.isActive ? 'نشط' : 'غير نشط'}</td>
                    <td>${item.isInventoryFrozen ? 'نعم' : 'لا'}</td>
                    <td>${item.createdOn ? moment(item.createdOn).format('DD/MM/YYYY') : '—'}</td>
                </tr>`).join('');
            const html = `
<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
    <meta charset="UTF-8" />
    <title>قائمة المخازن</title>
    <style>
        body { font-family: 'Tajawal', Arial, sans-serif; padding: 24px; color: #333; }
        h2 { text-align: center; margin-bottom: 1rem; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ddd; padding: 6px 8px; font-size: 12px; }
        th { background: #f5f5f5; }
        .meta { text-align: center; color: #777; font-size: 11px; margin-bottom: 1rem; }
    </style>
</head>
<body>
    <h2>قائمة المخازن</h2>
    <div class="meta">تاريخ التصدير: ${moment().format('DD/MM/YYYY HH:mm')}</div>
    <table>
        <thead>
            <tr>
                <th>#</th><th>اسم المخزن</th><th>الجهة</th><th>القسم</th>
                <th>النوع</th><th>الحالة</th><th>مجمد للجرد</th><th>تاريخ الإنشاء</th>
            </tr>
        </thead>
        <tbody>${rows}</tbody>
    </table>
    <script>window.onload = () => { setTimeout(() => window.print(), 250); };<\/script>
</body>
</html>`;
            const win = window.open('', '_blank');
            if (win) {
                win.document.open();
                win.document.write(html);
                win.document.close();
            }
        },
    },
};
